import { useListFilters, useListPagination } from "../../hooks/useListState.js";
import { useTabState } from "../../hooks/useTabState.js";
import { ACADEMY_TABS } from "../../lib/tabState.js";
import { listDate, validateListFilters } from "../../lib/listState.js";
import { isManager } from "../../lib/supabase.js";
import { RouteTargetNotice } from "../../components/RouteTargetNotice.jsx";
import { StaffAccounts } from "./AccountSettings.jsx";
import { TERM_CLASSIFICATION_OPTIONS, classificationLabel } from "../../utils/courseTerms.js";
import { SchoolSettings } from "./SchoolSettings.jsx";
import { schoolDisplayName } from "../../utils/schoolDisplay.js";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { BookOpen, Pencil, Plus, RotateCcw, Trash2, UserRoundCheck } from "lucide-react";
import { Alert, Button, Card, Checkbox, Col, DatePicker, Flex, Form, Input, InputNumber, Modal, Radio, Row, Select, Space, Switch, Table, Tabs, Tag, TimePicker, message } from "antd";
import {
  CURRENT_YEAR,
  COURSE_TERM_OPTIONS,
  GRADE_OPTIONS,
  SCHOOL_LEVELS,
  getCourseGradeOnlyLabel,
  getCourseTimeLabel,
  getStudentGradeLabel,
  getInitialGrade,
  getSubjectName,
  getTeacherName,
  isEnrollmentActiveOnDate,
  normalizeCourseForm
} from "../../utils/academy.js";
import {
  DEFAULT_SCHOOL_REGION,
  DEFAULT_SCHOOL_REGION_ID,
  getAcademySchoolOptions,
  getAdditionalSchoolOptions,
  getRegionId,
  getRegionLabel,
  getSchoolCities,
  getSchoolDistricts,
  matchesSchoolSearch
} from "../../data/schools.js";
import { WEEKDAYS } from "../../utils/date.js";
import { AppEmpty, StatusTag } from "../../components/Feedback.jsx";

const { TextArea } = Input;

const blankTeacher = { name: "", displayName: "", phone: "", status: "active", memo: "", active: true };
const blankSubject = { name: "", status: "active", active: true };
const defaultAcademyInfo = {
  name: "",
  province: "서울특별시",
  district: "강동구",
  address: "",
  phone: "",
  operatingRegionIds: [DEFAULT_SCHOOL_REGION_ID],
  additionalSchoolNames: []
};
const blankCourse = { year: CURRENT_YEAR, term: "semester_1", courseType: "regular", startDate: null, endDate: null, schoolLevel: "중등", grade: "1", subjectId: "", className: "", textbook: "", teacherId: "", days: ["월"], day: "월", timeRange: null, startTime: "", endTime: "", clinicDays: [], clinicTimeRange: null, clinicStartTime: "", clinicEndTime: "", capacity: "", tuition: "", active: true, status: "active" };
const statusOptions = [{ value: "active", label: "활성" }, { value: "inactive", label: "비활성" }];
const courseTypeOptions = [{ value: "regular", label: "정규" }, { value: "special", label: "특강" }];
const teacherStatusOptions = [{ value: "active", label: "근무" }, { value: "inactive", label: "퇴사" }];
const courseStatusFilterOptions = [...statusOptions, { value: "all", label: "전체" }];
const defaultCourseFilter = { year: CURRENT_YEAR, term: "", schoolLevel: "", grade: "", subjectId: "", status: "active" };
const timeFormat = "HH:mm";
const KAKAO_POSTCODE_SCRIPT_ID = "kakao-postcode-script";
const KAKAO_POSTCODE_SCRIPT_URL = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";

let kakaoPostcodeScript;

function toOptions(values) {
  return values.map((value) => ({ value, label: value }));
}

function activeValue(active) {
  return active === false ? "inactive" : "active";
}

function activeFromValue(value) {
  return value === "active";
}

function formatPhoneNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (digits.startsWith("02")) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function loadKakaoPostcode() {
  if (window.kakao?.Postcode || window.daum?.Postcode) return Promise.resolve();
  if (kakaoPostcodeScript) return kakaoPostcodeScript;

  kakaoPostcodeScript = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(KAKAO_POSTCODE_SCRIPT_ID);
    const script = existingScript || document.createElement("script");
    script.id = KAKAO_POSTCODE_SCRIPT_ID;
    script.src = KAKAO_POSTCODE_SCRIPT_URL;
    script.onload = resolve;
    script.onerror = () => reject(new Error("주소 검색 서비스를 불러오지 못했습니다."));
    if (!existingScript) document.head.appendChild(script);
  });

  return kakaoPostcodeScript;
}

function getCourseDays(course) {
  if (Array.isArray(course?.days) && course.days.length) return course.days;
  return course?.day ? [course.day] : [];
}

function parseTimeValue(value) {
  const [hour, minute] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
}

function toTimeRange(course) {
  if (!course?.startTime || !course?.endTime) return null;
  const start = parseTimeValue(course.startTime);
  const end = parseTimeValue(course.endTime);
  return start && end ? [start, end] : null;
}

function toDateValue(value) {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function getCourseDateLabel(value) {
  const date = toDateValue(value);
  return date ? date.format("YYYY.MM.DD") : "-";
}

function renderStatusTag(active, activeLabel = "활성", inactiveLabel = "비활성") {
  return <StatusTag tone={active === false ? "neutral" : "positive"}>{active === false ? inactiveLabel : activeLabel}</StatusTag>;
}

function clickableRow(onClick) {
  return (record) => ({ onClick: () => onClick(record), style: { cursor: "pointer" } });
}

function assignById(items, id, values) {
  const item = items.find((target) => target.id === id);
  if (item) Object.assign(item, values);
}

export function AcademyManagementPage({ store, mode = "academy", navigation }) {
  const { state, update, createId } = store;
  const manager = isManager(store.context.role);
  const courseOnly = mode === "courses";
  const teachers = state.teachers || [];
  const subjects = state.subjects || [];
  const academyInfo = { ...defaultAcademyInfo, ...(state.academyInfo || {}) };
  const courseTerms = state.courseTerms || COURSE_TERM_OPTIONS;
  const courses = state.courseGroups || [];
  const enrollments = state.studentEnrollments || [];
  const [tab, setTab] = useTabState("tab:academy", "teachers", manager ? ACADEMY_TABS : ACADEMY_TABS.filter(key => key !== "instructors"));
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [subjectModalOpen, setSubjectModalOpen] = useState(false);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseDeleteTargetId, setCourseDeleteTargetId] = useState("");
  const [academyInfoEditable, setAcademyInfoEditable] = useState(false);
  const [postcodeModalOpen, setPostcodeModalOpen] = useState(false);
  const [academyRegionIds, setAcademyRegionIds] = useState(academyInfo.operatingRegionIds);
  const [academyAdditionalSchoolNames, setAcademyAdditionalSchoolNames] = useState(academyInfo.additionalSchoolNames);
  const [regionCity, setRegionCity] = useState(DEFAULT_SCHOOL_REGION.city);
  const [regionDistrict, setRegionDistrict] = useState(DEFAULT_SCHOOL_REGION.district);
  const [editingTeacherId, setEditingTeacherId] = useState("");
  const [editingSubjectId, setEditingSubjectId] = useState("");
  const [editingCourseId, setEditingCourseId] = useState("");
  const [courseFormEditable, setCourseFormEditable] = useState(false);
  const [instructorReference, setInstructorReference] = useListFilters("list:instructors:filter", { date: dayjs().format("YYYY-MM-DD") },
    v => ({ date: listDate(v.date, dayjs().format("YYYY-MM-DD")) }));
  const instructorReferenceDate = useMemo(() => dayjs(instructorReference.date), [instructorReference.date]);
  const setInstructorReferenceDate = date => setInstructorReference({ date: date.format("YYYY-MM-DD") });
  const [instructorStudentList, setInstructorStudentList] = useState(null);
  const [expandedInstructorKeys, setExpandedInstructorKeys] = useState([]);
  const [teacherAntForm] = Form.useForm();
  const [subjectAntForm] = Form.useForm();
  const [courseAntForm] = Form.useForm();
  const [academyInfoForm] = Form.useForm();
  const postcodeContainerRef = useRef(null);
  const courseSchoolLevel = Form.useWatch("schoolLevel", courseAntForm) || blankCourse.schoolLevel;
  const courseType = Form.useWatch("courseType", courseAntForm) || blankCourse.courseType;
  const courseFormValues = courseAntForm.getFieldsValue(true);
  const validateCourseFilters = (v) => {
    const next = validateListFilters(v, defaultCourseFilter, {
      year: (store.loading || !!store.error) ? null : [CURRENT_YEAR, ...courses.map(c => c.year || CURRENT_YEAR)],
      term: [...TERM_CLASSIFICATION_OPTIONS.map(o => o.value),
        ...((store.loading || store.error || courses.some(c => c.termType === "other")) ? ["other"] : [])],
      schoolLevel: SCHOOL_LEVELS, status: ["active", "inactive", "all"],
      subjectId: (store.loading || !!store.error) ? null : subjects.map(s => s.id),
    });
    return validateListFilters(next, defaultCourseFilter, { grade: next.schoolLevel ? GRADE_OPTIONS[next.schoolLevel] || [] : Object.values(GRADE_OPTIONS).flat() });
  };
  const [courseFilterInput, setCourseFilterInput] = useListFilters("list:courses:input", defaultCourseFilter, validateCourseFilters);
  const [courseFilter, setCourseFilter] = useListFilters("list:courses:filter", defaultCourseFilter, validateCourseFilters);
  const [showCourseMeta, setShowCourseMeta] = useState(false);
  const [courseSaveError, setCourseSaveError] = useState("");
  const [courseSavedWarning, setCourseSavedWarning] = useState("");
  const courseFeedbackRef = useRef(null);
  useEffect(() => {
    if (!courseSaveError && !courseSavedWarning) return;
    const frame = requestAnimationFrame(() => {
      const target = courseFeedbackRef.current;
      const body = target?.closest(".ant-modal-body");
      if (body) body.scrollTop = 0;
      target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [courseSaveError, courseSavedWarning]);
  const courseMetaSwitchId = useId();
  const routeCourseId = navigation?.route.kind === "course" ? navigation.route.id : "";
  useEffect(() => {
    if (!routeCourseId || store.loading) return;
    const course = courses.find(item => item.id === routeCourseId);
    if (course) openCourseForm(course);
  }, [routeCourseId, store.loading]);

  useEffect(() => {
    if (!postcodeModalOpen) return undefined;

    let cancelled = false;
    loadKakaoPostcode()
      .then(() => {
        if (cancelled || !postcodeContainerRef.current) return;
        const Postcode = window.kakao?.Postcode || window.daum?.Postcode;
        new Postcode({
          oncomplete: (data) => {
            academyInfoForm.setFieldValue("address", data.address);
            setPostcodeModalOpen(false);
          }
        }).embed(postcodeContainerRef.current);
      })
      .catch((error) => {
        message.error(error.message || "주소 검색을 불러오지 못했습니다. 직접 입력해 주세요.");
        setPostcodeModalOpen(false);
      });

    return () => {
      cancelled = true;
    };
  }, [academyInfoForm, postcodeModalOpen]);

  const activeTeachers = useMemo(() => teachers.filter((teacher) => teacher.active !== false), [teachers]);
  const activeSubjects = useMemo(() => subjects.filter((subject) => subject.active !== false), [subjects]);
  const teacherOptions = useMemo(() => [{ value: "", label: "선택" }, ...activeTeachers.map((teacher) => ({ value: teacher.id, label: teacher.displayName || teacher.name }))], [activeTeachers]);
  const subjectOptions = useMemo(() => [{ value: "", label: "선택" }, ...activeSubjects.map((subject) => ({ value: subject.id, label: subject.name }))], [activeSubjects]);
  const regionCities = useMemo(() => getSchoolCities(), []);
  const regionDistricts = useMemo(() => getSchoolDistricts(regionCity), [regionCity]);
  const additionalSchoolOptions = useMemo(() => getAdditionalSchoolOptions({
    operatingRegionIds: academyRegionIds,
    additionalSchoolNames: academyAdditionalSchoolNames
  }), [academyAdditionalSchoolNames, academyRegionIds]);
  const gradeFilterOptions = useMemo(() => {
    if (courseFilterInput.schoolLevel === "재수") return [{ value: "", label: "전체 학년" }];
    const grades = courseFilterInput.schoolLevel
      ? GRADE_OPTIONS[courseFilterInput.schoolLevel] || []
      : [...new Set(Object.values(GRADE_OPTIONS).flat())].sort((a, b) => Number(a) - Number(b));
    return [{ value: "", label: "전체 학년" }, ...grades.map((grade) => ({ value: grade, label: `${grade}학년` }))];
  }, [courseFilterInput.schoolLevel]);
  const enrollmentCountByCourse = useMemo(() => enrollments.reduce((counts, enrollment) => {
    if (enrollment.active === false) return counts;
    counts[enrollment.courseGroupId] = (counts[enrollment.courseGroupId] || 0) + 1;
    return counts;
  }, {}), [enrollments]);
  const enrollmentHistoryCountByCourse = useMemo(() => enrollments.reduce((counts, enrollment) => {
    counts[enrollment.courseGroupId] = (counts[enrollment.courseGroupId] || 0) + 1;
    return counts;
  }, {}), [enrollments]);
  const courseDeleteTarget = courses.find((course) => course.id === courseDeleteTargetId) || null;
  const courseDeleteTargetName = courseDeleteTarget
    ? [getSubjectName(subjects, courseDeleteTarget.subjectId, courseDeleteTarget.subject), courseDeleteTarget.className].filter(Boolean).join(" ") || "선택한 수업"
    : "선택한 수업";
  const courseDeleteHistoryCount = courseDeleteTarget ? enrollmentHistoryCountByCourse[courseDeleteTarget.id] || 0 : 0;
  const courseYears = useMemo(() => [...new Set([CURRENT_YEAR, ...courses.map((course) => course.year || CURRENT_YEAR)])].sort((a, b) => b.localeCompare(a)), [courses]);
  const filteredCourses = useMemo(() => courses
    .filter((course) => (course.year || CURRENT_YEAR) === courseFilter.year)
    .filter((course) => !courseFilter.term || course.termType === courseFilter.term)
    .filter((course) => !courseFilter.schoolLevel || course.schoolLevel === courseFilter.schoolLevel)
    .filter((course) => !courseFilter.grade || course.grade === courseFilter.grade)
    .filter((course) => !courseFilter.subjectId || course.subjectId === courseFilter.subjectId)
    .filter((course) => courseFilter.status === "all" || (courseFilter.status === "active" ? course.active !== false : course.active === false)),
  [courseFilter, courses]);
  const coursePagination = useListPagination("list:courses:page", filteredCourses.length, courseFilter, store.loading || !!store.error);
  const teacherPagination = useListPagination("list:teachers:page", teachers.length, null, store.loading || !!store.error);
  const subjectPagination = useListPagination("list:subjects:page", subjects.length, null, store.loading || !!store.error);
  const instructorSummaries = useMemo(() => {
    const referenceDate = instructorReferenceDate.format("YYYY-MM-DD");
    const coursesById = new Map(courses.map((course) => [course.id, course]));
    const studentsById = new Map((state.students || []).map((student) => [student.id, student]));
    const enrollmentsByTeacher = new Map();

    enrollments.forEach((enrollment) => {
      if (!isEnrollmentActiveOnDate(enrollment, referenceDate)) return;
      const course = coursesById.get(enrollment.courseGroupId || enrollment.courseId);
      if (!course?.teacherId || !studentsById.has(enrollment.studentId)) return;
      const teacherEnrollments = enrollmentsByTeacher.get(course.teacherId) || [];
      teacherEnrollments.push({ enrollment, course, student: studentsById.get(enrollment.studentId) });
      enrollmentsByTeacher.set(course.teacherId, teacherEnrollments);
    });

    return teachers.map((teacher) => {
      const teacherEnrollments = enrollmentsByTeacher.get(teacher.id) || [];
      const coursesByIdForTeacher = new Map();
      teacherEnrollments.forEach((item) => {
        const existing = coursesByIdForTeacher.get(item.course.id) || { ...item.course, enrollments: [] };
        existing.enrollments.push(item);
        coursesByIdForTeacher.set(item.course.id, existing);
      });
      const courseRows = [...coursesByIdForTeacher.values()].map((course) => ({
        ...course,
        studentCount: course.enrollments.length,
        appliedTuitionTotal: course.enrollments.reduce((sum, item) => sum + Number(item.enrollment.appliedTuition || 0), 0)
      }));
      return {
        ...teacher,
        key: teacher.id,
        courseRows,
        courseCount: courseRows.length,
        studentCount: teacherEnrollments.length,
        appliedTuitionTotal: teacherEnrollments.reduce((sum, item) => sum + Number(item.enrollment.appliedTuition || 0), 0)
      };
    });
  }, [courses, enrollments, instructorReferenceDate, state.students, teachers]);

  function closeTeacherModal() {
    setTeacherModalOpen(false);
    setEditingTeacherId("");
    teacherAntForm.resetFields();
  }

  function closeSubjectModal() {
    setSubjectModalOpen(false);
    setEditingSubjectId("");
    subjectAntForm.resetFields();
  }

  function closeCourseModal() {
    if (store.saving) return;
    if (routeCourseId) { navigation.close(); return; }
    setCourseModalOpen(false);
    setEditingCourseId("");
    setCourseFormEditable(false);
    courseAntForm.resetFields();
  }

  function openTeacherForm(teacher) {
    setEditingTeacherId(teacher?.id || "");
    teacherAntForm.setFieldsValue(teacher ? { ...blankTeacher, ...teacher, status: activeValue(teacher.active) } : blankTeacher);
    setTeacherModalOpen(true);
  }

  async function saveTeacher(values) {
    const payload = {
      ...values,
      active: activeFromValue(values.status),
      displayName: values.displayName || values.name
    };
    if (!await update((draft) => {
      if (editingTeacherId) {
        assignById(draft.teachers, editingTeacherId, payload);
      } else {
        draft.teachers.push({ id: createId("teacher"), ...payload });
      }
    })) return;
    closeTeacherModal();
  }

  function openSubjectForm(subject) {
    setEditingSubjectId(subject?.id || "");
    subjectAntForm.setFieldsValue(subject ? { ...blankSubject, ...subject, status: activeValue(subject.active) } : blankSubject);
    setSubjectModalOpen(true);
  }

  async function saveSubject(values) {
    const payload = { ...values, active: activeFromValue(values.status) };
    if (!await update((draft) => {
      if (editingSubjectId) {
        assignById(draft.subjects, editingSubjectId, payload);
      } else {
        draft.subjects.push({ id: createId("subject"), ...payload });
      }
    })) return;
    closeSubjectModal();
  }

  async function saveAcademyInfo(values) {
    if (!await update((draft) => {
      draft.academyInfo = {
        ...defaultAcademyInfo,
        ...values,
        operatingRegionIds: academyRegionIds,
        additionalSchoolNames: academyAdditionalSchoolNames
      };
    })) return;
    setAcademyInfoEditable(false);
    message.success("학원 정보를 저장했습니다.");
  }

  function startAcademyInfoEdit() {
    academyInfoForm.setFieldsValue({ ...defaultAcademyInfo, ...(state.academyInfo || {}) });
    setAcademyRegionIds(academyInfo.operatingRegionIds);
    setAcademyAdditionalSchoolNames(academyInfo.additionalSchoolNames);
    setRegionCity(DEFAULT_SCHOOL_REGION.city);
    setRegionDistrict(DEFAULT_SCHOOL_REGION.district);
    setAcademyInfoEditable(true);
  }

  function cancelAcademyInfoEdit() {
    academyInfoForm.setFieldsValue({ ...defaultAcademyInfo, ...(state.academyInfo || {}) });
    setAcademyRegionIds(academyInfo.operatingRegionIds);
    setAcademyAdditionalSchoolNames(academyInfo.additionalSchoolNames);
    setAcademyInfoEditable(false);
  }

  function addOperatingRegion() {
    const regionId = getRegionId({ city: regionCity, district: regionDistrict });
    if (!regionCity || !regionDistrict || academyRegionIds.includes(regionId)) return;
    const nextRegionIds = [...academyRegionIds, regionId];
    const regionalSchoolNames = new Set(getAcademySchoolOptions({
      operatingRegionIds: nextRegionIds,
      additionalSchoolNames: []
    }).map((school) => school.value));
    setAcademyRegionIds(nextRegionIds);
    setAcademyAdditionalSchoolNames(academyAdditionalSchoolNames.filter((name) => !regionalSchoolNames.has(name)));
  }

  function removeOperatingRegion(regionId) {
    setAcademyRegionIds(academyRegionIds.filter((id) => id !== regionId));
  }

  function addAdditionalSchool(schoolName) {
    if (!schoolName || academyAdditionalSchoolNames.includes(schoolName)) return;
    setAcademyAdditionalSchoolNames([...academyAdditionalSchoolNames, schoolName]);
  }

  function removeAdditionalSchool(schoolName) {
    setAcademyAdditionalSchoolNames(academyAdditionalSchoolNames.filter((name) => name !== schoolName));
  }

  function searchAcademyAddress() {
    setPostcodeModalOpen(true);
  }

  function openCourseForm(course, editable = false) {
    // Editing stays a transient form; saved detail URLs reopen in read mode.
    if (course && navigation && routeCourseId !== course.id && !editable) { navigation.open("course", course.id); return; }
    setCourseSaveError("");
    setCourseSavedWarning("");
    const next = course
      ? { ...blankCourse, ...course, courseType: course.courseType || "regular", startDate: toDateValue(course.startDate), endDate: toDateValue(course.endDate), subjectId: course.subjectId || activeSubjects.find((subject) => subject.name === course.subject)?.id || "", days: getCourseDays(course), timeRange: toTimeRange(course), capacity: String(course.capacity || ""), tuition: String(course.tuition || "") }
      : { ...blankCourse, year: CURRENT_YEAR, subjectId: activeSubjects[0]?.id || "", teacherId: activeTeachers[0]?.id || "" };
    next.termType = course?.termType || courseTerms.find(term => term.id === course?.term)?.termType || 'first_semester';
    next.clinicTimeRange = toTimeRange({ startTime: course?.clinicStartTime, endTime: course?.clinicEndTime });
    next.status = activeValue(next.active);
    if (next.schoolLevel !== "재수" && !next.grade) next.grade = getInitialGrade(next.schoolLevel);
    setEditingCourseId(course?.id || "");
    setCourseFormEditable(!course || editable);
    courseAntForm.setFieldsValue(next);
    setCourseModalOpen(true);
  }

  function setSchoolLevel(schoolLevel) {
    courseAntForm.setFieldsValue({ schoolLevel, grade: getInitialGrade(schoolLevel) });
  }

  function setCourseFilterSchoolLevel(schoolLevel) {
    const availableGrades = GRADE_OPTIONS[schoolLevel] || [];
    const nextFilter = {
      ...courseFilterInput,
      schoolLevel,
      grade: availableGrades.includes(courseFilterInput.grade) ? courseFilterInput.grade : ""
    };
    setCourseFilterInput(nextFilter);
    setCourseFilter(nextFilter);
  }

  function updateCourseFilter(values) {
    const nextFilter = { ...courseFilterInput, ...values };
    setCourseFilterInput(nextFilter);
    setCourseFilter(nextFilter);
  }

  function resetCourseFilters() {
    setCourseFilterInput(defaultCourseFilter);
    setCourseFilter(defaultCourseFilter);
  }

  async function saveCourse(values) {
    if (store.saving || courseSavedWarning || !manager) return;
    setCourseSaveError("");
    const [start, end] = values.timeRange || [];
    const startTime = start?.format(timeFormat) || "";
    const endTime = end?.format(timeFormat) || "";
    if (!startTime || !endTime) return showCourseFieldError("timeRange", "수업 시간을 입력하세요.");
    if (startTime >= endTime) return showCourseFieldError("timeRange", "끝나는 시간은 시작 시간보다 늦어야 합니다.");
    const [clinicStart, clinicEnd] = values.clinicTimeRange || [];
    const clinicStartTime = clinicStart?.format(timeFormat) || "";
    const clinicEndTime = clinicEnd?.format(timeFormat) || "";
    if (Boolean(clinicStartTime) !== Boolean(clinicEndTime)) return showCourseFieldError("clinicTimeRange", "클리닉 시작 시간과 종료 시간을 함께 입력하세요.");
    if (clinicStartTime && clinicStartTime >= clinicEndTime) return showCourseFieldError("clinicTimeRange", "클리닉 종료 시간은 시작 시간보다 늦어야 합니다.");

    const selectedSubject = subjects.find((subject) => subject.id === values.subjectId);
    const formValues = {
      ...values,
      day: values.days?.[0] || "",
      startTime,
      endTime,
      clinicDays: values.clinicDays || [],
      clinicStartTime,
      clinicEndTime,
      courseType: values.courseType || "regular",
      startDate: values.courseType === "special" ? values.startDate?.format("YYYY-MM-DD") || "" : "",
      endDate: values.courseType === "special" ? values.endDate?.format("YYYY-MM-DD") || "" : "",
      active: activeFromValue(values.status)
    };
    const payload = { ...normalizeCourseForm(formValues), subject: selectedSubject?.name || "", termLabel: classificationLabel(values.termType) };
    let savedButNotReloaded = false;
    const saved = await update((draft) => {
      if (editingCourseId) {
        assignById(draft.courseGroups, editingCourseId, payload);
      } else {
        draft.courseGroups.push({ id: createId("course"), ...payload });
      }
    }, {
      onError: (text) => setCourseSaveError(text || "수업을 저장하지 못했습니다. 입력 내용을 확인하고 다시 시도해 주세요."),
      onSavedWarning: (text) => { savedButNotReloaded = true; setCourseSavedWarning(text); },
    });
    if (!saved || savedButNotReloaded) return;
    closeCourseModal();
  }

  function showCourseFieldError(name, text) {
    courseAntForm.setFields([{ name, errors: [text] }]);
    courseAntForm.scrollToField(name, { block: "center", focus: true });
  }

  function openCourseDeleteModal(courseId) {
    if (!courses.some((course) => course.id === courseId)) return;
    setCourseDeleteTargetId(courseId);
  }

  function closeCourseDeleteModal() {
    setCourseDeleteTargetId("");
  }

  async function confirmCourseDeleteAction() {
    if (!courseDeleteTarget) return;

    if (courseDeleteHistoryCount) {
      if (courseDeleteTarget.active === false) {
        closeCourseDeleteModal();
        return;
      }
      if (!await update((draft) => {
        const target = (draft.courseGroups || []).find((item) => item.id === courseDeleteTarget.id);
        if (target) target.active = false;
      })) return;
      message.success(`${courseDeleteTargetName} 수업을 비활성으로 변경했습니다.`);
      closeCourseDeleteModal();
      return;
    }

    if (!await update((draft) => {
      draft.courseGroups = (draft.courseGroups || []).filter((item) => item.id !== courseDeleteTarget.id);
    })) return;
    if (editingCourseId === courseDeleteTarget.id) closeCourseModal();
    message.success(`${courseDeleteTargetName} 수업을 비활성으로 변경했습니다.`);
    closeCourseDeleteModal();
  }

  const teacherColumns = [
    { title: "이름", dataIndex: "name" },
    { title: "표시명", render: (_, teacher) => teacher.displayName || teacher.name },
    { title: "연락처", render: (_, teacher) => teacher.phone || "-" },
    { title: "메모", render: (_, teacher) => teacher.memo || "-" },
    { title: "상태", render: (_, teacher) => renderStatusTag(teacher.active, "근무", "퇴사") }
  ];

  const subjectColumns = [
    { title: "과목명", dataIndex: "name" },
    { title: "상태", render: (_, subject) => renderStatusTag(subject.active) }
  ];

  const courseColumns = [
    { title: "년도", width: 58, align: "center", className: "course-list-meta-column", render: (_, course) => course.year || CURRENT_YEAR },
    { title: "구분", width: 68, align: "center", className: "course-list-meta-column", render: (_, course) => classificationLabel(course.termType) },
    { title: "유형", width: 56, align: "center", className: "course-list-meta-column", render: (_, course) => course.courseType === "special" ? "특강" : "정규" },
    { title: "과목", width: 56, align: "center", className: "course-list-meta-column", render: (_, course) => getSubjectName(subjects, course.subjectId, course.subject) },
    { title: "과정", width: 58, align: "center", render: (_, course) => course.schoolLevel || "-" },
    { title: "학년", width: 52, align: "center", render: (_, course) => getCourseGradeOnlyLabel(course) },
    { title: "반", width: 112, ellipsis: true, render: (_, course) => course.className || "-" },
    { title: "요일", width: 126, align: "center", render: (_, course) => `${getCourseDays(course).join(", ") || "-"} / ${course.clinicDays?.join(", ") || "-"}` },
    { title: "시간", width: 210, align: "center", render: (_, course) => `${getCourseTimeLabel(course) || "-"} / ${getCourseTimeLabel({ startTime: course.clinicStartTime, endTime: course.clinicEndTime }) || "-"}` },
    { title: "선생님", width: 76, align: "center", render: (_, course) => getTeacherName(teachers, course.teacherId) },
    { title: "수강 인원", width: 92, align: "center", render: (_, course) => `${enrollmentCountByCourse[course.id] || 0} / ${Number(course.capacity || 0) ? Number(course.capacity).toLocaleString("ko-KR") : "-"}명` },
    { title: "상태", width: 64, align: "center", render: (_, course) => renderStatusTag(course.active) },
    {
      title: "관리",
      width: 88,
      align: "center",
      fixed: "right",
      render: (_, course) => (
        <Space className="course-row-actions" size={2} onClick={(event) => event.stopPropagation()}>
          <Button type="text" icon={<Pencil size={15} />} disabled={!manager} onClick={() => openCourseForm(course, true)} aria-label={`${course.className || "수업"} 수정`} title="수정" />
          <Button
            type="text"
            className="course-row-delete-button"
            icon={<Trash2 size={15} />}
            disabled={!manager} onClick={() => openCourseDeleteModal(course.id)}
            aria-label={`${course.className || "수업"} 삭제`}
            title="삭제"
          />
        </Space>
      )
    }
  ];

  const courseSearchPanel = (
    <Card title="수업 검색" className="academy-management-card">
      <div className="student-filter-form">
        <div className="student-filter-inline">
        <div className={`student-filter-control${courseFilterInput.year !== CURRENT_YEAR ? " is-active" : ""}`}><span>년도:</span><Select value={courseFilterInput.year} onChange={(year) => updateCourseFilter({ year })} options={courseYears.map((year) => ({ value: year, label: year }))} /></div>
        <div className={`student-filter-control${courseFilterInput.term ? " is-active" : ""}`}><span>구분:</span><Select value={courseFilterInput.term} onChange={(term) => updateCourseFilter({ term })} options={[{ value: "", label: "전체 구분" }, ...TERM_CLASSIFICATION_OPTIONS, ...(courses.some(course => course.termType === 'other') ? [{value:'other',label:'기타 (기존)'}] : [])]} /></div>
        <div className={`student-filter-control${courseFilterInput.schoolLevel ? " is-active" : ""}`}><span>과정:</span><Select value={courseFilterInput.schoolLevel} onChange={setCourseFilterSchoolLevel} options={[{ value: "", label: "전체 과정" }, ...toOptions(SCHOOL_LEVELS)]} /></div>
        <div className={`student-filter-control${courseFilterInput.grade ? " is-active" : ""}`}><span>학년:</span><Select value={courseFilterInput.grade} onChange={(grade) => updateCourseFilter({ grade })} options={gradeFilterOptions} /></div>
        <div className={`student-filter-control${courseFilterInput.subjectId ? " is-active" : ""}`}><span>과목:</span><Select value={courseFilterInput.subjectId} onChange={(subjectId) => updateCourseFilter({ subjectId })} options={[{ value: "", label: "전체 과목" }, ...subjects.map((subject) => ({ value: subject.id, label: subject.name }))]} /></div>
        <div className="student-filter-control"><span>상태:</span><Select value={courseFilterInput.status} onChange={(status) => updateCourseFilter({ status })} options={courseStatusFilterOptions} /></div>
        <Button htmlType="button" className="student-filter-reset" onClick={resetCourseFilters} icon={<RotateCcw size={14} />}>필터 초기화</Button>
        </div>
      </div>
    </Card>
  );

  const coursePanel = (
    <div className="page-stack">
      {courseSearchPanel}
      <Card className="academy-management-card course-list-card">
        <div className="academy-tab-panel">
          <div className="section-toolbar">
            <h3>수업 목록</h3>
            <Space className="section-toolbar-actions" size={8} wrap>
              <Flex align="center" gap={8} style={{ minHeight: 32 }}>
                <label htmlFor={courseMetaSwitchId} style={{ fontSize: 13 }}>년도·구분·유형 표시</label>
                <Switch id={courseMetaSwitchId} checked={showCourseMeta} onChange={setShowCourseMeta} aria-label="년도·구분·유형 표시" />
              </Flex>
              <Button className="student-list-action-button" type="primary" icon={<Plus size={16} />} disabled={!manager} onClick={() => openCourseForm()}>수업 추가</Button>
            </Space>
          </div>
          <Table
            className="management-list-table course-list-table"
            rowKey="id"
            columns={showCourseMeta ? courseColumns : courseColumns.slice(3)}
            dataSource={filteredCourses}
            locale={{ emptyText: <AppEmpty description="조건에 맞는 수업이 없습니다." /> }}
            pagination={coursePagination}
            scroll={{ x: showCourseMeta ? 1240 : 1058 }}
            onRow={clickableRow(openCourseForm)}
          />
        </div>
      </Card>
    </div>
  );

  const instructorSummaryColumns = [
    { title: "강사명", render: (_, teacher) => teacher.displayName || teacher.name },
    { title: "담당 수업반 수", dataIndex: "courseCount", align: "right", render: (count) => `${count}개` },
    { title: "전체 수강생 수", dataIndex: "studentCount", align: "right", render: (count) => `${count}명` },
    { title: "적용 수강료 합계", dataIndex: "appliedTuitionTotal", align: "right", render: (value) => `${Number(value || 0).toLocaleString("ko-KR")}원` }
  ];

  const instructorCourseColumns = [
    { title: "수업 유형", render: (_, course) => <Tag color={course.courseType === "special" ? "gold" : "blue"}>{course.courseType === "special" ? "특강" : "정규"}</Tag> },
    { title: "수업명", render: (_, course) => `${getSubjectName(subjects, course.subjectId, course.subject)} ${course.className || ""}`.trim() || "-" },
    { title: "수강생 수", dataIndex: "studentCount", align: "right", render: (count) => `${count}명` },
    { title: "적용 수강료 합계", dataIndex: "appliedTuitionTotal", align: "right", render: (value) => `${Number(value || 0).toLocaleString("ko-KR")}원` },
    { title: "", align: "right", render: (_, course) => <Button onClick={() => setInstructorStudentList(course)}>학생 명단 보기</Button> }
  ];

  const instructorStudentColumns = [
    { title: "학생 이름", render: (_, item) => item.student.name },
    { title: "학교 / 학년", render: (_, item) => [schoolDisplayName(item.student.school, academyInfo), getStudentGradeLabel(item.student)].filter(Boolean).join(" / ") || "-" },
    { title: "수강 시작일", render: (_, item) => item.enrollment.startDate || item.enrollment.startMonth || "-" },
    { title: "학생별 적용 수강료", align: "right", render: (_, item) => `${Number(item.enrollment.appliedTuition || 0).toLocaleString("ko-KR")}원` },
    { title: "수강 상태", render: () => <Tag color="success">수강 중</Tag> }
  ];

  const instructorPagination = useListPagination("list:instructors:page", instructorSummaries.length, instructorReferenceDate.format("YYYY-MM-DD"), store.loading || !!store.error);
  const instructorPanel = (
    <div className="academy-tab-panel">
      <div className="section-toolbar instructor-summary-toolbar">
        <h3>강사별 수업 현황</h3>
        <Space className="section-toolbar-actions" size={8} wrap>
          <Space className="instructor-reference-control" size={7}>
            <span>기준일</span>
            <DatePicker aria-label="강사별 수업 기준일" value={instructorReferenceDate} onChange={(value) => setInstructorReferenceDate(value || dayjs())} format="YYYY.MM.DD" allowClear={false} />
          </Space>
        </Space>
      </div>
      <Table
        className="management-list-table instructor-summary-table"
        rowKey="id"
        columns={instructorSummaryColumns}
        dataSource={instructorSummaries}
        pagination={instructorPagination}
        scroll={{ x: 720 }}
        expandable={{
          expandedRowKeys: expandedInstructorKeys,
          onExpandedRowsChange: setExpandedInstructorKeys,
          rowExpandable: (teacher) => teacher.courseRows.length > 0,
          expandedRowRender: (teacher) => <Table className="management-subtable" rowKey="id" columns={instructorCourseColumns} dataSource={teacher.courseRows} pagination={false} size="small" scroll={{ x: 680 }} />
        }}
        onRow={(teacher) => ({
          onClick: (event) => {
            if (!teacher.courseRows.length || event.target.closest(".ant-table-row-expand-icon")) return;
            setExpandedInstructorKeys((keys) => keys.includes(teacher.id) ? keys.filter((key) => key !== teacher.id) : [...keys, teacher.id]);
          },
          onKeyDown: (event) => {
            if (!teacher.courseRows.length || (event.key !== "Enter" && event.key !== " ")) return;
            event.preventDefault();
            setExpandedInstructorKeys((keys) => keys.includes(teacher.id) ? keys.filter((key) => key !== teacher.id) : [...keys, teacher.id]);
          },
          role: teacher.courseRows.length ? "button" : undefined,
          tabIndex: teacher.courseRows.length ? 0 : undefined,
          style: teacher.courseRows.length ? { cursor: "pointer" } : undefined
        })}
        locale={{ emptyText: <AppEmpty description="등록된 강사가 없습니다." /> }}
      />
    </div>
  );

  const tabItems = [
    {
      key: "subjects",
      label: "과목 관리",
      children: (
        <div className="academy-tab-panel">
          <div className="section-toolbar">
            <h3>과목 목록</h3>
            <Space className="section-toolbar-actions" size={8} wrap>
              <Button type="primary" icon={<BookOpen size={16} />} onClick={() => openSubjectForm()}>과목 추가</Button>
            </Space>
          </div>
          <Table
            className="management-list-table subject-list-table"
            rowKey="id"
            columns={subjectColumns}
            dataSource={subjects}
            locale={{ emptyText: <AppEmpty description="등록된 과목이 없습니다." /> }}
            pagination={subjectPagination}
            scroll={{ x: 420 }}
            onRow={clickableRow(openSubjectForm)}
          />
        </div>
      )
    },
    {
      key: "teachers",
      label: "강사 관리",
      children: (
        <div className="academy-tab-panel">
          <div className="section-toolbar">
            <h3>강사 목록</h3>
            <Space className="section-toolbar-actions" size={8} wrap>
              <Button type="primary" icon={<UserRoundCheck size={16} />} onClick={() => openTeacherForm()}>강사 추가</Button>
            </Space>
          </div>
          <Table
            className="management-list-table teacher-list-table"
            rowKey="id"
            columns={teacherColumns}
            dataSource={teachers}
            locale={{ emptyText: <AppEmpty description="등록된 강사가 없습니다." /> }}
            pagination={teacherPagination}
            scroll={{ x: 720 }}
            onRow={clickableRow(openTeacherForm)}
          />
        </div>
      )
    },
    {
      key: "instructors",
      label: "강사별 수업 현황",
      children: instructorPanel
    },
    {
      key: "schools",
      label: "학교 관리",
      children: <SchoolSettings store={store} />
    }
  ];

  if (routeCourseId && !courses.some(item => item.id === routeCourseId)) return <RouteTargetNotice loading={store.loading} onBack={() => navigation.close()} />;
  return (
    <div className="page-stack">
      {!courseOnly && (
        <Card
          className="academy-management-card academy-info-card"
          title="우리 학원 정보"
          extra={academyInfoEditable ? (
            <Space>
              <Button onClick={cancelAcademyInfoEdit}>취소</Button>
              <Button type="primary" onClick={() => academyInfoForm.submit()}>저장</Button>
            </Space>
          ) : (
            <Button icon={<Pencil size={16} />} disabled title="현재 서버는 학원 기본정보 수정 API를 제공하지 않습니다." onClick={startAcademyInfoEdit}>수정</Button>
          )}
        >
          <Form
            form={academyInfoForm}
            layout="vertical"
            size="medium"
            style={{ fontWeight: 400 }}
            initialValues={academyInfo}
            onFinish={saveAcademyInfo}
          >
            <div className="course-detail-table-wrap academy-info-table-wrap">
              <table className="course-detail-table course-edit-table academy-info-table">
                <colgroup><col /><col /><col /><col /></colgroup>
                <tbody>
                  <tr className="course-detail-section-row"><th colSpan={4}>기본 정보</th></tr>
                  <tr>
                    <th scope="row">학원명</th>
                    <td>{academyInfoEditable ? <Form.Item name="name" className="course-table-form-item" rules={[{ required: true, message: "학원명을 입력해 주세요." }]}><Input placeholder="학원명을 입력하세요" /></Form.Item> : academyInfo.name || "-"}</td>
                    <th scope="row">학원 연락처</th>
                    <td>{academyInfoEditable ? <Form.Item name="phone" className="course-table-form-item" rules={[{ required: true, message: "대표 연락처를 입력해 주세요." }]} normalize={formatPhoneNumber}><Input placeholder="010-0000-0000" /></Form.Item> : academyInfo.phone || "-"}</td>
                  </tr>
                  <tr>
                    <th scope="row">시·도</th>
                    <td>{academyInfo.province || "-"}</td>
                    <th scope="row">구</th>
                    <td>{academyInfo.district || "-"}</td>
                  </tr>
                  <tr>
                    <th scope="row">상세 주소</th>
                    <td colSpan={3}>{academyInfoEditable ? <Space.Compact style={{ width: "100%" }}><Form.Item name="address" className="course-table-form-item" rules={[{ required: true, message: "상세 주소를 입력해 주세요." }]} noStyle><Input placeholder="도로명, 건물명, 층수 등을 입력하세요" /></Form.Item><Button onClick={searchAcademyAddress}>주소 검색</Button></Space.Compact> : academyInfo.address || "-"}</td>
                  </tr>
                  <tr className="course-detail-section-row"><th colSpan={4}>학교 설정</th></tr>
                  <tr>
                    <th scope="row">운영 지역</th>
                    <td colSpan={3}>
                      {false ? (
                        <Flex vertical gap={8}>
                          <Space.Compact style={{ width: "100%" }}>
                            <Select value={regionCity} onChange={(city) => { setRegionCity(city); setRegionDistrict(getSchoolDistricts(city)[0] || ""); }} options={regionCities.map((city) => ({ value: city, label: city }))} style={{ width: "42%" }} />
                            <Select value={regionDistrict} onChange={setRegionDistrict} options={regionDistricts.map((district) => ({ value: district, label: district }))} style={{ width: "42%" }} />
                            <Button onClick={addOperatingRegion}>추가</Button>
                          </Space.Compact>
                          <Space wrap>
                            {academyRegionIds.map((regionId) => <Tag key={regionId} closable onClose={() => removeOperatingRegion(regionId)}>{getRegionLabel(regionId)}</Tag>)}
                          </Space>
                        </Flex>
                      ) : (
                        <Space wrap>{academyInfo.operatingRegionIds.map((regionId) => <Tag key={regionId}>{getRegionLabel(regionId)}</Tag>)}</Space>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">추가 학교</th>
                    <td colSpan={3}>
                      {false ? (
                        <Flex vertical gap={8}>
                          <Select
                            value={undefined}
                            onChange={addAdditionalSchool}
                            options={additionalSchoolOptions}
                            placeholder="학교 검색"
                            showSearch
                            filterOption={matchesSchoolSearch}
                            notFoundContent="추가할 수 있는 학교가 없습니다."
                          />
                          <Space wrap>
                            {academyAdditionalSchoolNames.map((schoolName) => <Tag key={schoolName} closable onClose={() => removeAdditionalSchool(schoolName)}>{schoolDisplayName(schoolName, academyInfo)}</Tag>)}
                          </Space>
                        </Flex>
                      ) : (
                        academyInfo.additionalSchoolNames.length ? <Space wrap>{academyInfo.additionalSchoolNames.map((schoolName) => <Tag key={schoolName}>{schoolDisplayName(schoolName, academyInfo)}</Tag>)}</Space> : "-"
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Form>
        </Card>
      )}
      {courseOnly ? coursePanel : <Card className="academy-management-card academy-settings-card"><Tabs className="academy-tabs" activeKey={tab} onChange={setTab} items={tabItems.filter((item) => item.key !== "instructors" || manager)} /></Card>}

      {!courseOnly && <StaffAccounts context={store.context} />}
      <Modal
        title="주소 검색"
        open={postcodeModalOpen}
        footer={null}
        onCancel={() => setPostcodeModalOpen(false)}
        width={520}
        className="postcode-search-modal"
        destroyOnHidden
      >
        <div ref={postcodeContainerRef} style={{ minHeight: 480 }} />
      </Modal>

      <Modal
        title={editingTeacherId ? "선생님 수정" : "선생님 추가"}
        open={teacherModalOpen}
        onCancel={closeTeacherModal}
        footer={[
          <Button key="cancel" onClick={closeTeacherModal}>취소</Button>,
          <Button key="save" type="primary" onClick={() => teacherAntForm.submit()}>저장</Button>
        ]}
        width={560}
        className="academy-form-modal teacher-management-modal"
        destroyOnHidden
      >
        <Form className="academy-modal-form" form={teacherAntForm} layout="vertical" size="medium" onFinish={saveTeacher} initialValues={blankTeacher}>
          <Row gutter={[12, 16]}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="이름" rules={[{ required: true, message: "선생님 이름을 입력하세요." }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="displayName" label="표시명">
                <Input placeholder="박T" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="phone" label="연락처">
            <Input />
          </Form.Item>
          <Form.Item name="status" label="상태">
            <Radio.Group block options={teacherStatusOptions} optionType="button" buttonStyle="solid" />
          </Form.Item>
          <Form.Item name="memo" label="메모">
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingSubjectId ? "과목 수정" : "과목 추가"}
        open={subjectModalOpen}
        onCancel={closeSubjectModal}
        footer={[
          <Button key="cancel" onClick={closeSubjectModal}>취소</Button>,
          <Button key="save" type="primary" onClick={() => subjectAntForm.submit()}>저장</Button>
        ]}
        width={520}
        className="academy-form-modal subject-management-modal"
        destroyOnHidden
      >
        <Form className="academy-modal-form" form={subjectAntForm} layout="vertical" size="medium" onFinish={saveSubject} initialValues={blankSubject}>
          <Form.Item name="name" label="과목명" rules={[{ required: true, message: "과목명을 입력하세요." }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="상태">
            <Radio.Group block options={statusOptions} optionType="button" buttonStyle="solid" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingCourseId ? (courseFormEditable ? "수업 수정" : "수업 정보") : "수업 추가"}
        open={courseModalOpen}
        onCancel={closeCourseModal}
        closable={!store.saving}
        maskClosable={!store.saving}
        keyboard={!store.saving}
        footer={(
          courseFormEditable ? (
            <Space className="modal-footer-actions" size={8} wrap>
              <Button disabled={store.saving} onClick={closeCourseModal}>{courseSavedWarning ? "닫기" : "취소"}</Button>
              <Button type="primary" loading={store.saving} disabled={store.saving || Boolean(courseSavedWarning)} onClick={() => courseAntForm.submit()}>저장</Button>
            </Space>
          ) : (
            <Space>
              <Button size="middle" onClick={closeCourseModal}>취소</Button>
              <Button size="middle" type="primary" disabled={!manager} onClick={() => setCourseFormEditable(true)}>수정</Button>
            </Space>
          )
        )}
        width={760}
        className="academy-form-modal course-management-modal"
        destroyOnHidden
      >
        {(courseSaveError || courseSavedWarning) && <div ref={courseFeedbackRef} tabIndex={-1} style={{ marginBottom: 16 }}>
          <Alert type={courseSaveError ? "error" : "warning"} showIcon role="alert" message={courseSaveError || courseSavedWarning} />
        </div>}
        {courseFormEditable ? (
          <Form className="academy-course-form" form={courseAntForm} layout="vertical" size="medium" disabled={store.saving || Boolean(courseSavedWarning)} onFinish={saveCourse}
            onFinishFailed={({ errorFields }) => { if (errorFields[0]) courseAntForm.scrollToField(errorFields[0].name, { block: "center", focus: true }); }} initialValues={blankCourse}>
            <div className="academy-course-table-sections">
              <section className="academy-course-table-section">
                <h3>수업 기본정보</h3>
                <div className="course-detail-table-wrap">
                  <table className="course-detail-table course-edit-table">
                    <colgroup><col /><col /><col /><col /></colgroup>
                    <tbody>
                      <tr>
                        <th scope="row">유형</th>
                        <td><Form.Item name="courseType" className="course-table-form-item"><Select options={courseTypeOptions} /></Form.Item></td>
                        <th scope="row">과정</th>
                        <td><Form.Item name="schoolLevel" className="course-table-form-item"><Select onChange={setSchoolLevel} options={toOptions(SCHOOL_LEVELS)} /></Form.Item></td>
                      </tr>
                      <tr>
                        <th scope="row">학년</th>
                        <td>{courseSchoolLevel === "재수" ? "-" : <Form.Item name="grade" className="course-table-form-item"><Select options={toOptions(GRADE_OPTIONS[courseSchoolLevel] || [])} /></Form.Item>}</td>
                        <th scope="row">과목</th>
                        <td><Form.Item name="subjectId" className="course-table-form-item" rules={[{ required: true, message: "과목을 선택하세요." }]}><Select options={subjectOptions} /></Form.Item></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="academy-course-table-section">
                <h3>수업 일정</h3>
                <div className="course-detail-table-wrap">
                  <table className="course-detail-table course-edit-table">
                    <colgroup><col /><col /><col /><col /></colgroup>
                    <tbody>
                      <tr>
                        <th scope="row">반 이름</th>
                        <td><Form.Item name="className" className="course-table-form-item" rules={[{ required: true, message: "반 이름을 입력하세요." }]}><Input placeholder="기본반, 심화반, 화목반" /></Form.Item></td>
                        <th scope="row">선생님</th>
                        <td><Form.Item name="teacherId" className="course-table-form-item" rules={[{ required: true, message: "선생님을 선택하세요." }]}><Select options={teacherOptions} /></Form.Item></td>
                      </tr>
                      <tr>
                        <th scope="row">수업 요일</th>
                        <td><Form.Item name="days" className="course-table-form-item" rules={[{ required: true, message: "요일을 선택하세요." }]}><Checkbox.Group options={WEEKDAYS.map((day) => ({ value: day, label: day }))} /></Form.Item></td>
                        <th scope="row">수업 시간</th>
                        <td><Form.Item name="timeRange" className="course-table-form-item" rules={[{ required: true, message: "수업 시간을 입력하세요." }]}><TimePicker.RangePicker format={timeFormat} minuteStep={5} style={{ width: "100%" }} /></Form.Item></td>
                      </tr>
                      <tr>
                        <th scope="row">클리닉 요일</th>
                        <td><Form.Item name="clinicDays" className="course-table-form-item"><Checkbox.Group options={WEEKDAYS.map((day) => ({ value: day, label: day }))} /></Form.Item></td>
                        <th scope="row">클리닉 시간</th>
                        <td><Form.Item name="clinicTimeRange" className="course-table-form-item" rules={[{ validator: (_, range) => {
                          if (!range) return Promise.resolve();
                          if (!range[0] || !range[1]) return Promise.reject(new Error("시작 시간과 종료 시간을 함께 입력하세요."));
                          return range[0].format(timeFormat) < range[1].format(timeFormat) ? Promise.resolve() : Promise.reject(new Error("종료 시간은 시작 시간보다 늦어야 합니다."));
                        } }]}><TimePicker.RangePicker format={timeFormat} minuteStep={5} style={{ width: "100%" }} /></Form.Item></td>
                      </tr>
                      {courseType === "special" && (
                        <tr>
                          <th scope="row">시작일</th>
                          <td><Form.Item name="startDate" className="course-table-form-item" rules={[{ required: true, message: "시작일을 입력하세요." }]}><DatePicker format="YYYY.MM.DD" style={{ width: "100%" }} /></Form.Item></td>
                          <th scope="row">종료일</th>
                          <td><Form.Item name="endDate" className="course-table-form-item" rules={[{ required: true, message: "종료일을 입력하세요." }]}><DatePicker format="YYYY.MM.DD" style={{ width: "100%" }} /></Form.Item></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="academy-course-table-section">
                <h3>운영 설정</h3>
                <div className="course-detail-table-wrap">
                  <table className="course-detail-table course-edit-table">
                    <colgroup><col /><col /><col /><col /></colgroup>
                    <tbody>
                      <tr>
                        <th scope="row">수강 인원</th>
                        <td colSpan={3}><Space size={8}><span>{`${enrollmentCountByCourse[editingCourseId] || 0}명 /`}</span><Form.Item name="capacity" className="course-table-form-item"><InputNumber min={0} /></Form.Item><span>명</span></Space></td>
                      </tr>
                      <tr>
                        <th scope="row">교재</th>
                        <td colSpan={3}><Form.Item name="textbook" className="course-table-form-item"><Input disabled placeholder="서버에서 지원하지 않는 항목" /></Form.Item></td>
                      </tr>
                      <tr>
                        <th scope="row">년도</th>
                        <td><Form.Item name="year" className="course-table-form-item" rules={[{ required: true, message: "년도를 입력하세요." }]}><InputNumber min={2000} max={2200} style={{ width: "100%" }} /></Form.Item></td>
                        <th scope="row">구분</th>
                        <td><Form.Item name="termType" className="course-table-form-item" rules={[{ required: true, message: "구분을 선택하세요." }]}><Select options={[...TERM_CLASSIFICATION_OPTIONS, ...(courseFormValues.termType === 'other' ? [{value:'other',label:'기타 (기존)'}] : [])]} placeholder="구분 선택" /></Form.Item></td>
                      </tr>
                      <tr>
                        <th scope="row">기본 수강료</th>
                        <td><Form.Item name="tuition" className="course-table-form-item"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item></td>
                        <th scope="row">상태</th>
                        <td><Form.Item name="status" className="course-table-form-item" rules={[{ required: true, message: "상태를 선택하세요." }]}><Radio.Group block options={statusOptions} optionType="button" buttonStyle="solid" /></Form.Item></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </Form>
        ) : (
          <div className="academy-course-view academy-course-table-sections">
            <section className="academy-course-table-section">
              <h3>수업 기본정보</h3>
              <div className="course-detail-table-wrap">
                <table className="course-detail-table">
                  <colgroup><col /><col /><col /><col /></colgroup>
                  <tbody>
                    <tr><th scope="row">유형</th><td>{courseFormValues.courseType === "special" ? "특강" : "정규"}</td><th scope="row">과정</th><td>{courseFormValues.schoolLevel || "-"}</td></tr>
                    <tr><th scope="row">학년</th><td>{courseFormValues.schoolLevel === "재수" ? "해당 없음" : courseFormValues.grade ? `${courseFormValues.grade}학년` : "-"}</td><th scope="row">과목</th><td>{getSubjectName(subjects, courseFormValues.subjectId) || "-"}</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="academy-course-table-section">
              <h3>수업 일정</h3>
              <div className="course-detail-table-wrap">
                <table className="course-detail-table">
                  <colgroup><col /><col /><col /><col /></colgroup>
                  <tbody>
                    <tr><th scope="row">반 이름</th><td>{courseFormValues.className || "-"}</td><th scope="row">선생님</th><td>{getTeacherName(teachers, courseFormValues.teacherId) || "-"}</td></tr>
                    <tr><th scope="row">수업 요일</th><td>{courseFormValues.days?.join(", ") || "-"}</td><th scope="row">수업 시간</th><td>{courseFormValues.timeRange?.map((time) => time.format(timeFormat)).join("~") || "-"}</td></tr>
                    <tr><th scope="row">클리닉 요일</th><td>{courseFormValues.clinicDays?.join(", ") || "-"}</td><th scope="row">클리닉 시간</th><td>{courseFormValues.clinicTimeRange?.map((time) => time.format(timeFormat)).join("~") || "-"}</td></tr>
                    {courseFormValues.courseType === "special" && <tr><th scope="row">시작일</th><td>{getCourseDateLabel(courseFormValues.startDate)}</td><th scope="row">종료일</th><td>{getCourseDateLabel(courseFormValues.endDate)}</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="academy-course-table-section">
              <h3>운영 설정</h3>
              <div className="course-detail-table-wrap">
                <table className="course-detail-table">
                  <colgroup><col /><col /><col /><col /></colgroup>
                  <tbody>
                    <tr><th scope="row">수강 인원</th><td colSpan={3}>{editingCourseId ? `${enrollmentCountByCourse[editingCourseId] || 0} / ${Number(courseFormValues.capacity || 0) ? Number(courseFormValues.capacity).toLocaleString("ko-KR") : "-"}명` : "-"}</td></tr>
                    <tr><th scope="row">교재</th><td colSpan={3}>{courseFormValues.textbook || "-"}</td></tr>
                    <tr><th scope="row">년도</th><td>{courseFormValues.year || CURRENT_YEAR}</td><th scope="row">구분</th><td>{classificationLabel(courseFormValues.termType)}</td></tr>
                    <tr><th scope="row">기본 수강료</th><td>{Number(courseFormValues.tuition || 0) ? `${Number(courseFormValues.tuition).toLocaleString("ko-KR")}원` : "-"}</td><th scope="row">상태</th><td>{renderStatusTag(courseFormValues.status !== "inactive")}</td></tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </Modal>
      <Modal
        title={courseDeleteHistoryCount
          ? (courseDeleteTarget?.active === false ? `${courseDeleteTargetName} 수업은 이미 비활성 상태입니다` : `${courseDeleteTargetName} 수업을 비활성으로 변경할까요?`)
          : `${courseDeleteTargetName} 수업을 비활성화할까요?`}
        open={Boolean(courseDeleteTarget)}
        onCancel={closeCourseDeleteModal}
        width={460}
        centered
        destroyOnHidden
        footer={(
          <Space className="modal-footer-actions" size={8}>
            {!(courseDeleteHistoryCount && courseDeleteTarget?.active === false) && <Button onClick={closeCourseDeleteModal}>취소</Button>}
            <Button
              type="primary"
              danger={!courseDeleteHistoryCount}
              onClick={courseDeleteHistoryCount && courseDeleteTarget?.active === false ? closeCourseDeleteModal : confirmCourseDeleteAction}
            >
              {courseDeleteHistoryCount ? (courseDeleteTarget?.active === false ? "확인" : "비활성으로 변경") : "비활성화"}
            </Button>
          </Space>
        )}
      >
        <div className="course-delete-modal-content">
          {courseDeleteHistoryCount ? (
            <>
              <p>이 수업에는 수강 이력 <strong>{courseDeleteHistoryCount}건</strong>이 연결되어 있습니다.</p>
              <p>{courseDeleteTarget?.active === false
                ? "과거 기록 보존을 위해 삭제할 수 없으며, 현재 비활성 상태로 관리 중입니다."
                : "과거 기록 보존을 위해 삭제할 수 없습니다. 삭제 대신 비활성 상태로 변경할 수 있습니다."}</p>
            </>
          ) : (
            <>
              <p>이 수업을 비활성화합니다.</p>
              <p>기존 수강 기록은 보존됩니다.</p>
            </>
          )}
        </div>
      </Modal>
      <Modal
        title={instructorStudentList ? `${getSubjectName(subjects, instructorStudentList.subjectId, instructorStudentList.subject)} ${instructorStudentList.className || ""} 학생 명단`.trim() : "학생 명단"}
        open={Boolean(instructorStudentList)}
        onCancel={() => setInstructorStudentList(null)}
        footer={<Button onClick={() => setInstructorStudentList(null)}>닫기</Button>}
        width={860}
        className="instructor-student-modal"
      >
        <Table
          rowKey={(item) => item.enrollment.id}
          columns={instructorStudentColumns}
          dataSource={instructorStudentList?.enrollments || []}
          pagination={false}
        />
      </Modal>
    </div>
  );
}
